import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Bell, Send, Check, AlertCircle, Clock, AlertTriangle, CreditCard, Shield, FileText, RefreshCw, CheckCheck, Archive, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tipoLabels = {
  richiesta_disponibilita: 'Richiesta Disponibilità',
  promemoria_evento: 'Promemoria Evento',
  conferma_presenza: 'Conferma Presenza',
  scadenza_documento_identita: 'Documento Identità',
  scadenza_copertura: 'Copertura Assicurativa',
  scadenza_documento_compliance: 'Documento Compliance'
};

const statoColors = {
  da_inviare: 'bg-yellow-100 text-yellow-800',
  inviata: 'bg-blue-100 text-blue-800',
  letta: 'bg-green-100 text-green-800',
  da_leggere: 'bg-orange-100 text-orange-800',
  archiviata: 'bg-slate-100 text-slate-600',
  errore: 'bg-red-100 text-red-800'
};

const statoLabels = {
  da_inviare: 'Da Inviare',
  inviata: 'Inviata',
  letta: 'Letta',
  da_leggere: 'Da leggere',
  archiviata: 'Archiviata',
  errore: 'Errore'
};

const prioritaColors = {
  alta: 'bg-red-500 text-white',
  media: 'bg-amber-500 text-white',
  bassa: 'bg-blue-500 text-white'
};

export default function Notifiche() {
  const navigate = useNavigate();
  const [notifiche, setNotifiche] = useState([]);
  const [scadenze, setScadenze] = useState({ alta: [], media: [], bassa: [], totale: 0 });
  const [riepilogo, setRiepilogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [tipoFilter, setTipoFilter] = useState('all');
  const [statoFilter, setStatoFilter] = useState('all');

  useEffect(() => {
    fetchAll();
  }, [tipoFilter, statoFilter]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchNotifiche(), fetchScadenze(), fetchRiepilogo()]);
    setLoading(false);
  };

  const fetchNotifiche = async () => {
    try {
      let url = `${API_URL}/api/notifiche`;
      const params = new URLSearchParams();
      if (tipoFilter !== 'all') params.append('tipo', tipoFilter);
      if (statoFilter !== 'all') params.append('stato', statoFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setNotifiche(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchScadenze = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifiche/scadenze`, { credentials: 'include' });
      if (response.ok) {
        setScadenze(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchRiepilogo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifiche/riepilogo-scadenze`, { credentials: 'include' });
      if (response.ok) {
        setRiepilogo(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const generaAlertScadenze = async () => {
    setGenerando(true);
    try {
      const response = await fetch(`${API_URL}/api/notifiche/genera-alert-scadenze?giorni_anticipo=30`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchAll();
      } else {
        toast.error('Errore nella generazione');
      }
    } catch (error) {
      toast.error('Errore nella generazione');
    } finally {
      setGenerando(false);
    }
  };

  const segnaLetta = async (notificaId) => {
    try {
      await fetch(`${API_URL}/api/notifiche/${notificaId}/letta`, {
        method: 'PUT',
        credentials: 'include'
      });
      fetchScadenze();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const archiviaNotifica = async (notificaId) => {
    try {
      await fetch(`${API_URL}/api/notifiche/${notificaId}/archivia`, {
        method: 'PUT',
        credentials: 'include'
      });
      toast.success('Notifica archiviata');
      fetchScadenze();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const segnaTutteLette = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifiche/segna-tutte-lette`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchScadenze();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getStatoIcon = (stato) => {
    switch (stato) {
      case 'inviata': return <Send className="w-4 h-4" />;
      case 'letta': return <Check className="w-4 h-4" />;
      case 'errore': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getScadenzaIcon = (tipo) => {
    switch (tipo) {
      case 'scadenza_documento_identita': return <CreditCard className="w-4 h-4" />;
      case 'scadenza_copertura': return <Shield className="w-4 h-4" />;
      case 'scadenza_documento_compliance': return <FileText className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  // Stats for general notifications
  const stats = {
    totali: notifiche.length,
    daInviare: notifiche.filter(n => n.stato === 'da_inviare').length,
    inviate: notifiche.filter(n => n.stato === 'inviata').length,
    lette: notifiche.filter(n => n.stato === 'letta').length,
    errori: notifiche.filter(n => n.stato === 'errore').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifiche-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Notifiche</h1>
        <p className="page-subtitle">Gestione comunicazioni e alert scadenze</p>
      </div>

      <Tabs defaultValue="scadenze" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="scadenze" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alert Scadenze
            {riepilogo?.alert_critici > 0 && (
              <Badge className="bg-red-500 text-white ml-1">{riepilogo.alert_critici}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="comunicazioni" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Comunicazioni
          </TabsTrigger>
        </TabsList>

        {/* TAB: ALERT SCADENZE */}
        <TabsContent value="scadenze" className="mt-6">
          {/* Riepilogo Scadenze */}
          {riepilogo && (
            <div className="grid gap-4 md:grid-cols-3 mb-6">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Documenti Identità</p>
                      <div className="flex gap-3 mt-2">
                        {riepilogo.documenti_identita.scaduti > 0 && (
                          <span className="text-red-600 font-bold">{riepilogo.documenti_identita.scaduti} scaduti</span>
                        )}
                        {riepilogo.documenti_identita.entro_7_giorni > 0 && (
                          <span className="text-amber-600">{riepilogo.documenti_identita.entro_7_giorni} entro 7gg</span>
                        )}
                        {riepilogo.documenti_identita.entro_30_giorni > 0 && (
                          <span className="text-blue-600">{riepilogo.documenti_identita.entro_30_giorni} entro 30gg</span>
                        )}
                        {riepilogo.documenti_identita.scaduti === 0 && riepilogo.documenti_identita.entro_7_giorni === 0 && riepilogo.documenti_identita.entro_30_giorni === 0 && (
                          <span className="text-green-600">Tutto ok</span>
                        )}
                      </div>
                    </div>
                    <CreditCard className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Coperture Assicurative</p>
                      <div className="flex gap-3 mt-2">
                        {riepilogo.coperture_assicurative.scadute > 0 && (
                          <span className="text-red-600 font-bold">{riepilogo.coperture_assicurative.scadute} scadute</span>
                        )}
                        {riepilogo.coperture_assicurative.entro_7_giorni > 0 && (
                          <span className="text-amber-600">{riepilogo.coperture_assicurative.entro_7_giorni} entro 7gg</span>
                        )}
                        {riepilogo.coperture_assicurative.entro_30_giorni > 0 && (
                          <span className="text-blue-600">{riepilogo.coperture_assicurative.entro_30_giorni} entro 30gg</span>
                        )}
                        {riepilogo.coperture_assicurative.scadute === 0 && riepilogo.coperture_assicurative.entro_7_giorni === 0 && riepilogo.coperture_assicurative.entro_30_giorni === 0 && (
                          <span className="text-green-600">Tutto ok</span>
                        )}
                      </div>
                    </div>
                    <Shield className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Documenti Compliance</p>
                      <div className="flex gap-3 mt-2">
                        {riepilogo.documenti_compliance.scaduti > 0 && (
                          <span className="text-red-600 font-bold">{riepilogo.documenti_compliance.scaduti} scaduti</span>
                        )}
                        {riepilogo.documenti_compliance.entro_7_giorni > 0 && (
                          <span className="text-amber-600">{riepilogo.documenti_compliance.entro_7_giorni} entro 7gg</span>
                        )}
                        {riepilogo.documenti_compliance.entro_30_giorni > 0 && (
                          <span className="text-blue-600">{riepilogo.documenti_compliance.entro_30_giorni} entro 30gg</span>
                        )}
                        {riepilogo.documenti_compliance.scaduti === 0 && riepilogo.documenti_compliance.entro_7_giorni === 0 && riepilogo.documenti_compliance.entro_30_giorni === 0 && (
                          <span className="text-green-600">Tutto ok</span>
                        )}
                      </div>
                    </div>
                    <FileText className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mb-6">
            <Button onClick={generaAlertScadenze} disabled={generando} data-testid="btn-genera-alert">
              {generando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Aggiorna Alert Scadenze
            </Button>
            {scadenze.totale > 0 && (
              <Button variant="outline" onClick={segnaTutteLette}>
                <CheckCheck className="w-4 h-4 mr-2" />
                Segna tutte come lette
              </Button>
            )}
          </div>

          {/* Alert per priorità */}
          {scadenze.totale === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-700">Nessuna scadenza imminente</p>
                <p className="text-slate-500 mt-1">Tutti i documenti e le coperture sono in regola</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Alta priorità */}
              {scadenze.alta.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader className="bg-red-50 border-b border-red-100">
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Priorità Alta - Scadenza entro 7 giorni ({scadenze.alta.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {scadenze.alta.map((n) => (
                        <div key={n.notifica_id} className="p-4 flex items-center justify-between hover:bg-red-50/50">
                          <div className="flex items-center gap-3">
                            {getScadenzaIcon(n.tipo)}
                            <div>
                              <p className="font-medium">{n.titolo}</p>
                              <p className="text-sm text-slate-600">{n.messaggio}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={prioritaColors[n.priorita]}>{n.giorni_rimanenti} giorni</Badge>
                            <Button size="sm" variant="ghost" onClick={() => segnaLetta(n.notifica_id)}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => archiviaNotifica(n.notifica_id)}>
                              <Archive className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Media priorità */}
              {scadenze.media.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader className="bg-amber-50 border-b border-amber-100">
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <Clock className="w-5 h-5" />
                      Priorità Media - Scadenza entro 15 giorni ({scadenze.media.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {scadenze.media.map((n) => (
                        <div key={n.notifica_id} className="p-4 flex items-center justify-between hover:bg-amber-50/50">
                          <div className="flex items-center gap-3">
                            {getScadenzaIcon(n.tipo)}
                            <div>
                              <p className="font-medium">{n.titolo}</p>
                              <p className="text-sm text-slate-600">{n.messaggio}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={prioritaColors[n.priorita]}>{n.giorni_rimanenti} giorni</Badge>
                            <Button size="sm" variant="ghost" onClick={() => segnaLetta(n.notifica_id)}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => archiviaNotifica(n.notifica_id)}>
                              <Archive className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Bassa priorità */}
              {scadenze.bassa.length > 0 && (
                <Card className="border-blue-200">
                  <CardHeader className="bg-blue-50 border-b border-blue-100">
                    <CardTitle className="flex items-center gap-2 text-blue-700">
                      <Bell className="w-5 h-5" />
                      Priorità Bassa - Scadenza entro 30 giorni ({scadenze.bassa.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {scadenze.bassa.map((n) => (
                        <div key={n.notifica_id} className="p-4 flex items-center justify-between hover:bg-blue-50/50">
                          <div className="flex items-center gap-3">
                            {getScadenzaIcon(n.tipo)}
                            <div>
                              <p className="font-medium">{n.titolo}</p>
                              <p className="text-sm text-slate-600">{n.messaggio}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={prioritaColors[n.priorita]}>{n.giorni_rimanenti} giorni</Badge>
                            <Button size="sm" variant="ghost" onClick={() => segnaLetta(n.notifica_id)}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => archiviaNotifica(n.notifica_id)}>
                              <Archive className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* TAB: COMUNICAZIONI */}
        <TabsContent value="comunicazioni" className="mt-6">
          {/* Stats */}
          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#0055BB]">{stats.totali}</p>
                <p className="text-sm text-slate-500">Totali</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.daInviare}</p>
                <p className="text-sm text-slate-500">Da Inviare</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.inviate + stats.lette}</p>
                <p className="text-sm text-slate-500">Inviate</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.errori}</p>
                <p className="text-sm text-slate-500">Errori</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <Select value={tipoFilter} onValueChange={setTipoFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Tipo notifica" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    <SelectItem value="richiesta_disponibilita">Richiesta Disponibilità</SelectItem>
                    <SelectItem value="promemoria_evento">Promemoria Evento</SelectItem>
                    <SelectItem value="conferma_presenza">Conferma Presenza</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statoFilter} onValueChange={setStatoFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="da_inviare">Da Inviare</SelectItem>
                    <SelectItem value="inviata">Inviate</SelectItem>
                    <SelectItem value="letta">Lette</SelectItem>
                    <SelectItem value="errore">Errori</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#0055BB]" />
                Elenco Comunicazioni
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {notifiche.length === 0 ? (
                <div className="empty-state py-16">
                  <Bell className="empty-state-icon" />
                  <p className="empty-state-title">Nessuna comunicazione trovata</p>
                  <p className="empty-state-text">Le comunicazioni vengono create quando invii richieste di disponibilità o promemoria</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Messaggio</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Data Creazione</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifiche.filter(n => !n.tipo?.startsWith('scadenza_')).map((notifica, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge variant="outline">
                            {tipoLabels[notifica.tipo] || notifica.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <p className="text-sm truncate">{notifica.messaggio}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={statoColors[notifica.stato]}>
                            <span className="flex items-center gap-1">
                              {getStatoIcon(notifica.stato)}
                              {statoLabels[notifica.stato]}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(notifica.created_at).toLocaleString('it-IT')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info */}
      <div className="alert-info">
        <p className="font-medium mb-1">Sistema di Notifiche Automatiche</p>
        <ul className="text-sm space-y-1">
          <li>• Gli alert scadenze vengono generati automaticamente per documenti identità, coperture e compliance</li>
          <li>• Clicca "Aggiorna Alert Scadenze" per verificare nuove scadenze nei prossimi 30 giorni</li>
          <li>• Le notifiche sono raggruppate per priorità: Alta (7gg), Media (15gg), Bassa (30gg)</li>
          <li>• Puoi archiviare le notifiche gestite per tenerle nascoste</li>
        </ul>
      </div>
    </div>
  );
}
